// components/BuilderTimeline/BuilderActivityTimeline.tsx

import React, { useEffect, useMemo, useState } from 'react';
import { BuilderActivityRecord } from '../../providers/types';
import { useBuilderTimeline } from '../../hooks/useBuilderTimeline';
import { BuilderActivityItem } from './BuilderActivityItem';
import './BuilderTimeline.css';

interface BuilderTimelineProps {
  jobId: number;
  onEventCountChange?: (count: number) => void;
}

export const BuilderTimeline: React.FC<BuilderTimelineProps> = ({ jobId, onEventCountChange }) => {
  const { data: timelineData, isLoading: loading, error, refetch } = useBuilderTimeline(jobId);
  const [bonusType, setBonusType] = useState<string>(
    localStorage.getItem("cba.bonusType") || "ClosingBonus"
  );
  const eventCount = useMemo(() => {
    return (timelineData?.stage5Activities?.length || 0) + (timelineData?.closingActivities?.length || 0);
  }, [timelineData]);

  useEffect(() => {
    onEventCountChange?.(eventCount);
  }, [eventCount, onEventCountChange]);


  const renderActivitiesList = (activities: BuilderActivityRecord[], bonusType: string) => {
    if (!activities || activities.length === 0) {
      return (
        <div className="no-activities">
          <p>No {bonusType} builder activities found</p>
        </div>
      );
    }

    // Separate current builders from history
    const currentBuilders = activities.filter(a => a.recordType === 'CurrentBuilder');
    const historyActivities = activities.filter(a => a.recordType === 'HistoryActivity');

    return (
      <>
        {/* Current Builders Section */}
        {currentBuilders.length > 0 && (
          <div className="current-builders-section">
            {currentBuilders.map((activity, index) => (
              <BuilderActivityItem
                key={`current-${activity.builderId || index}`}
                activity={activity}
              />
            ))}
          </div>
        )}

        {/* Separator */}
        {currentBuilders.length > 0 && historyActivities.length > 0 && (
          <div className="timeline-separator">
            <div className="separator-line"></div>
            <div className="separator-label">ACTIVITY HISTORY</div>
            <div className="separator-line"></div>
          </div>
        )}

        {/* History Activities Section */}
        {historyActivities.length > 0 && (
          <div className="history-activities-section">
            {historyActivities.map((activity, index) => (
              <BuilderActivityItem
                key={`history-${activity.builderHistoryId || index}`}
                activity={activity}
              />
            ))}
          </div>
        )}
      </>
    );
  };

  if (loading) {
    return (
      <div className="builder-timeline-loading">
        <div className="spinner"></div>
        <p>Loading builder activity timeline...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="builder-timeline-error">
        <p>{error}</p>
        <button onClick={refetch} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  if (!timelineData) {
    return null;
  }

  const hasStage5 = timelineData.stage5Activities && timelineData.stage5Activities.length > 0;
  const hasClosing = timelineData.closingActivities && timelineData.closingActivities.length > 0;

  return (
    <div className="builder-activity-timeline">
      <h2 className="timeline-title">Builder Activity Timeline</h2>

      <div className="timeline-container">
        {/* Stage5 Column */}
        <div className="timeline-column stage5-column">
          <div className="column-header">
            <h3>Stage 5 Activities</h3>
            {hasStage5 && (
              <span className="activity-count">
                {timelineData.stage5Activities.length} {timelineData.stage5Activities.length === 1 ? 'record' : 'records'}
              </span>
            )}
          </div>
          <div className="column-content">
            {renderActivitiesList(timelineData.stage5Activities, 'Stage 5')}
          </div>
        </div>

        {/* Closing Column */}
        {(bonusType !== "Stage5Bonus") && (
          <div className="timeline-column closing-column">
            <div className="column-header">
              <h3>Closing Activities</h3>
              {hasClosing && (
                <span className="activity-count">
                  {timelineData.closingActivities.length} {timelineData.closingActivities.length === 1 ? 'record' : 'records'}
                </span>
              )}
            </div>
            <div className="column-content">
              {renderActivitiesList(timelineData.closingActivities, 'Closing')}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BuilderTimeline;